<script setup lang="ts">
import { computed, ref, watch } from "vue"
import { storeToRefs } from "pinia"
import { useRoute, useRouter } from "vue-router"
import { useToast } from "primevue/usetoast"
import { useSourceStore } from "@core/stores/source.store"
import {
    estRefusDacces,
    fetchErrorRedirectToast,
    isSourceSidebarRoute,
    restrictedAccessRedirectToast,
} from "@core/router/restrictedAccessNavigation"
import { redirectOnSourceViewsLinkRevoked } from "@core/router/sourceViewsLinkRevokedRedirect"

const route = useRoute()
const router = useRouter()
const toast = useToast()
const sourceStore = useSourceStore()
const { getSourceById, hasErrorSourceById, errorSourceById, sources } =
    storeToRefs(sourceStore)

/**
 * Dernier `_id_source` pour lequel `fetchSourceById` a terminé. `null` = chargement en cours
 * ou pas encore de cycle pour l’URL actuelle.
 */
const shellFetchSettledForRouteId = ref<string | null>(null)

/** Évite un flash de sous-page pendant la navigation après erreur API ou accès retiré (lien views révoqué). */
const isNavigatingHomeAfterSourceFetchError = ref(false)

watch(
    () => route.params._id_source,
    async (newId) => {
        if (!newId || typeof newId !== "string") {
            shellFetchSettledForRouteId.value = null
            return
        }
        shellFetchSettledForRouteId.value = null
        try {
            await sourceStore.fetchSourceById({ _id_source: newId })
            if (sourceStore.hasErrorSourceById) {
                if (
                    route.params._id_source === newId &&
                    isSourceSidebarRoute(router.currentRoute.value)
                ) {
                    isNavigatingHomeAfterSourceFetchError.value = true
                    try {
                        // Un 5xx n'est pas un refus d'accès : l'annoncer comme tel
                        // accuse l'utilisateur d'un problème de droits qu'il n'a pas.
                        const refus = estRefusDacces(errorSourceById.value)
                        const textes = refus
                            ? restrictedAccessRedirectToast.source
                            : fetchErrorRedirectToast.source
                        await router.push({ name: "Home", query: {} })
                        toast.add({
                            group: "notifications",
                            severity: refus ? "warn" : "error",
                            summary: textes.summary,
                            detail: textes.detail,
                            life: 5000,
                        })
                    } finally {
                        isNavigatingHomeAfterSourceFetchError.value = false
                    }
                }
                return
            }

            const fetched = getSourceById.value(newId)
            if (
                fetched?.company_source_views_link_revoked === true &&
                route.params._id_source === newId &&
                isSourceSidebarRoute(router.currentRoute.value)
            ) {
                await redirectOnSourceViewsLinkRevoked({
                    router,
                    toast,
                    sources,
                    _id_source: newId,
                    navigationBlockRef: isNavigatingHomeAfterSourceFetchError,
                })
                return
            }
        } finally {
            shellFetchSettledForRouteId.value = newId
        }
    },
    { immediate: true }
)

const routeSourceId = computed(
    () => route.params._id_source as string | undefined
)

/** Le store contient la source demandée par l’URL. */
const isSourceAlignedForRoute = computed(() => {
    const id = routeSourceId.value
    if (!id) return false
    return Boolean(getSourceById.value(id))
})

const shouldDeferChildForSourceFetch = computed(() => {
    const id = routeSourceId.value
    if (!id) return false
    if (isNavigatingHomeAfterSourceFetchError.value) return true
    if (isSourceAlignedForRoute.value) return false
    if (hasErrorSourceById.value) return false
    if (shellFetchSettledForRouteId.value === id) return false
    return true
})
</script>

<template>
    <LoadingApp v-if="shouldDeferChildForSourceFetch" class="page" />
    <router-view v-else-if="routeSourceId" />
</template>
